<template>
    <v-data-table
        v-if="files != null"
        :headers="headers"
        :items="files"
        :search="search"
        no-results-text="No files have been uploaded for this experiment yet."
        item-key="id"
        class="elevation-1"
        :items-per-page="25"
    >
        <template v-slot:item.filename="{ item }">
            <a :href="item.url" target="_blank">{{ item.filename }}</a>
        </template>
        <template v-slot:item.uploadDate="{ item }">
            {{ getDateTimeStringForDisplayInLocalTimeZone(item.updatedAt || item.createdAt) }}
        </template>
        <template v-slot:item.actions="{ item }">
            <v-icon v-if="userCanDeleteFile(item)" @click="deleteFile(item)">$delete</v-icon>
            <v-icon v-if="userCanModifyFile(item)" @click="editFile(item)">$edit</v-icon>
        </template>
    </v-data-table>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { VDataTableHeader } from '@/vuetifyTypeFixes';
import { ExperimentFile } from '@/services/files/filesAPI';
import { getDateTimeStringForDisplayInLocalTimeZone } from '@/lib/dateUtils';
import { useAuth } from "@/lib/ts/auth";

const auth = useAuth();

const authUser = computed(() => {
    return auth.getAuthUser();
});

const userCanDeleteAllFiles = computed(() => {
    return auth.isAuthorized("ExperimentFiles:DeleteAll");
});

const userCanModifyAllFiles = computed(() => {
    return auth.isAuthorized("ExperimentFiles:ModifyAll");
});

const { files, search } = defineProps<{
    files: ExperimentFile[],
    search: string,
}>();

const emit = defineEmits<{
    (e: 'deleteFile', file: ExperimentFile): void,
    (e: 'editFile', file: ExperimentFile): void,
}>();

const headers = ref<VDataTableHeader[]>([
    { title: "Filename", value: "filename", sortable: true },
    { title: "Category", value: "category", sortable: true },
    { title: "File Visibility", value: "visibility", sortable: true },
    { title: "Upload Date", value: "uploadDate", sortable: true },
    { title: "Uploaded By", value: "uploadedBy", sortable: true },
    { title: "Actions", value: "actions", sortable: false },
]);

function deleteFile(file: ExperimentFile) {
    emit("deleteFile", file);
}

function editFile(file: ExperimentFile) {
    emit("editFile", file);
}

function userCanDeleteFile(file: ExperimentFile) {
    return file.uploadedBy === authUser.value.email || userCanDeleteAllFiles.value;
}

function userCanModifyFile(file: ExperimentFile) {
    return file.uploadedBy === authUser.value.email || userCanModifyAllFiles.value;
}

</script>
